import React, { useContext, useEffect, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Button, Chip, Divider, Grid, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import SideBar from '../../uiParts/SideBar';

import { getMemberNews } from '../../../api/db/news';
import { getNews } from '../../../api/microcms/news';
import type { NewsType } from '../../../api/microcms/news';
import {
  gray100,
  gray70,
  gray90,
  primary10,
  primaryColor,
  red10,
  red100
} from '../../../constants/Colors';
import { sideBarWidth } from '../../../constants/Location';
import { AuthContext } from '../../../contexts/auth';
import { common } from '../../../modules/error';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  body: {
    margin: '24px 0 32px 0',
    color: gray100,
    '& .MuiTypography-root figure img': {
      width: '440px',
      height: 'auto'
    },
    '@media screen and (max-width:600px)': {
      '& .MuiTypography-root figure img': {
        width: '240px'
      }
    }
  },
  button: {
    color: primaryColor,
    mt: '48px'
  },
  wrapper: {
    '@media screen and (max-width:600px)': {
      margin: '24px 16px'
    },
    margin: '32px'
  },
  date: {
    color: gray90,
    fontSize: '16px'
  },
  divider: {
    bgcolor: gray70,
    border: 'none',
    flex: 1,
    height: '1px'
  },
  tag: {
    borderRadius: '26px',
    fontSize: '14px',
    height: '25px',
    fontWeight: 500,
    mr: '8px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '18px'
    },
    color: gray100,
    fontSize: '24px',
    fontWeight: 500
  },
  image: {
    height: '240px',
    width: '240px',
    mb: '16px'
  },
  detailButton: {
    display: 'block',
    p: '0',
    fontSize: '16px',
    color: primaryColor,
    textDecorationLine: 'underline',
    mb: '40px'
  }
};

const NewsDetail = (): React.ReactElement => {
  const navigate = useNavigate();
  const { type, newsId } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [news, setNews] = useState<NewsType>({
    id: '',
    important_flg: false,
    publishedAt: '',
    title: '',
    content: '',
    category: '',
    updatedAt: ''
  });

  const chipColor = (tag: string) => {
    switch (tag) {
      case '重要':
        return { color: red100, backgroundColor: red10 };
      default:
        return { color: primaryColor, backgroundColor: primary10 };
    }
  };

  const { checkLoginStatus } = useContext(AuthContext);
  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        if (!checkLoginStatus) {
          navigate('/auth/login');
          return;
        }
        const result = await checkLoginStatus();
        if (!result.check) {
          navigate('/auth/login');
          return;
        }

        if (!newsId) {
          throw new Error();
        }

        switch (type) {
          // 一般情報
          case 'general':
            if (!newsId) {
              throw new Error();
            }
            // お知らせ(一般情報)詳細取得
            const fetchedGeneralNews = await getNews(newsId);
            // 日付の表示形式を変更
            const publishedData = fetchedGeneralNews.publishedAt.split('T')[0];
            const formattedPublishedData = publishedData
              .slice(0, 10)
              .replace(/-/g, '/');

            const displayGeneralNews = {
              ...fetchedGeneralNews,
              publishedAt: formattedPublishedData
            };
            setNews(displayGeneralNews);
            break;

          // 会員個別情報
          case 'member':
            // お知らせ(会員個別情報)詳細取得
            const response = await getMemberNews(newsId);
            const fetchedMemberNews = response.data.member_news;
            // 日付の表示形式を変更
            const updateDate = fetchedMemberNews.updated_at.split('T')[0];
            const formattedUpdateDate = updateDate
              .slice(0, 10)
              .replace(/-/g, '/');

            const displayMemberNews = {
              ...fetchedMemberNews,
              publishedAt: formattedUpdateDate
            };
            setNews(displayMemberNews);
            break;

          default:
            throw new Error();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        setErrorText(common);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <CustomHead title={`お知らせ ${news.title} | CMCOS[コムコス]`} />
      <Grid>
        <SideBar />
        <Grid
          sx={{
            '@media screen and (min-width:961px)': {
              ml: sideBarWidth
            }
          }}
        >
          <CustomLoading loading={loading} />
          <Box sx={styles.wrapper}>
            <Grid container flexDirection={'column'} rowGap={'16px'}>
              <Grid container flexDirection={'row'}>
                {news.important_flg && (
                  <Chip
                    label={'重要'}
                    sx={{ ...chipColor('重要'), ...styles.tag }}
                  />
                )}
                {news.category && (
                  <Chip
                    label={news.category}
                    sx={{ ...chipColor(news.category), ...styles.tag }}
                  />
                )}
                <Typography sx={styles.date}>{news.publishedAt}</Typography>
              </Grid>
              <Typography sx={styles.title}>{news.title}</Typography>
              <Grid container flexDirection={'row'} alignItems={'flex-end'}>
                <Divider sx={styles.divider} />
              </Grid>
            </Grid>
            <Grid sx={styles.body}>
              <Typography
                component="p"
                dangerouslySetInnerHTML={{ __html: news.content }}
                mb={'40px'}
              />
            </Grid>
            <Button
              sx={styles.button}
              onClick={() => {
                if (type === 'member') {
                  navigate('/news/member');
                } else {
                  navigate('/news');
                }
              }}
            >
              <ChevronLeftIcon sx={{ mr: '8px' }} />
              戻る
            </Button>
          </Box>
          <CustomSnackbar
            autoHideDuration={6000}
            open={open}
            onClose={() => setOpen(false)}
            text={errorText}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default NewsDetail;
